import { Button } from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { ICardElementWithChildren } from "backend/model/model";
import clsx from "clsx";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { t } from "i18next";
import DepartmentPopover from "../popups/DepartmentPopover";
import useTopElements from "backend/hooks/model/useTopElements";

const useDivisionCardStyles = makeThemedStyles("DivisionCard", (theme) => ({
  root: {
    width: "250px",
    borderRadius: "2px",
    color: "white",
  },
  card: {
    width: "100%",
    height: "130px",
    borderRadius: "1.6em",
    overflow: "hidden",
    position: "relative",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.37) -0.1%, rgba(16, 41, 117, 0.83) 83.43%)",
  },
  content: {
    width: "100%",
    padding: "1.9em 2em 1.4em 2em",
    height: "calc(100% - 3.8em)",
    cursor: "pointer",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.17) -0.1%, rgba(16, 41, 117, 0.83) 83.43%)",
  },
  type: {
    color: "rgba(245, 246, 246, 1)",
    fontSize: "1.2em",
    lineHeight: "1.25",
    fontWeight: 500,
    marginBottom: "0.66em",
  },
  name: {
    color: "rgba(16, 41, 117, 1)",
    fontSize: "1.4em",
    lineHeight: "1.428",
    fontWeight: 600,
    letterSpacing: "0.05625px",
    maxWidth: "15.07em",
  },
  title: {
    color: "white",
  },
  cardActions: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "3.8em",
  },
  cardActionsButton: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "100%",
    color: "rgb(31, 52, 87)",
    fontSize: "1.2em",
    borderRadius: 0,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 1.5833em",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  disabledCardActionsButton: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "100%",
    color: "rgb(140, 142, 148)",
    fontSize: "1.2em",
    fontStyle: "italic",
    borderRadius: 0,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 1.5833em",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  depTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    color: "rgba(16, 41, 117, 1)",
    fontSize: "1.4em",
    fontWeight: "bold",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  disabledDepTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    color: "rgb(140, 142, 148)",
    fontSize: "1.4em",
    fontWeight: "bold",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  icon: {
    width: "30px",
    height: "30px",
  },
  item: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "10px",
    justifyContent: "flex-start",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
}));
export const PopoverItem = (
  props: React.PropsWithChildren<{
    element: ICardElementWithChildren;
    translatedName: string;
    cardTitle?: string;
    pointerStyle?:string,
    hasRight?: boolean
    departmentIcon?: string;
    rtl?: boolean;
    onSelect?: () => void;
    onSelectChild?: (item: ICardElementWithChildren) => void;
  }>
) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const styles = useDivisionCardStyles();
  const { data: topElements, isLoading: topElementsLoading } = useTopElements();
  const childrenDirect = React.useMemo(
    () => props.element.children,
    [props.element]
  );
  const departmentCount = childrenDirect.length;
  const accessibleDepartmentCount = childrenDirect.filter(f=> f?.name !== undefined).length;
  return (
    <div
      className={styles.root}
      role="button"
      style={{
        cursor: props.onSelect ? props.pointerStyle : undefined,
      }}
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={() => {
        if (props.onSelect && !showPopover) {
          props.onSelect();
        }
      }}
    >
      <div className={styles.item}>
        {props.departmentIcon ? (
          <img className={styles.icon} src={props.departmentIcon} />
        ) : null}
        <div className={clsx(props.hasRight ? styles.depTitle : styles.disabledDepTitle, props.rtl && "RTL")}>
          {props.translatedName}
        </div>
      </div>
      <Button
        onClick={(e) => {
          if (childrenDirect.length > 0) {
            setShowPopover(true);
          }
          e.preventDefault();
          e.stopPropagation();
        }}
        className={clsx(accessibleDepartmentCount > 0 ? styles.cardActionsButton :styles.disabledCardActionsButton , props.rtl && "RTL")}
        endIcon={childrenDirect.length > 0 ? <ExpandMore /> : null}
        ref={ref}
      >
        {departmentCount > 0 && (
          <>
            {departmentCount}{" "}
            {departmentCount === 1
              ? t("cascade.Department")
              : t("cascade.Departments")}
          </>
        )}
      </Button>
      <DepartmentPopover
        onClose={() => {
          setShowPopover(false);
        }}
        onSelect={props.onSelect}
        onSelectChild={props.onSelectChild}
        departmentIcon={props.departmentIcon}
        items={childrenDirect}        
        anchor={ref.current}
        typenames={["Scorecard"]}
        isOpen={showPopover}
      />
    </div>
  );
};
